import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, apiUrl, ApiError } from './api'
import { useApp } from './store'
import type { Member } from './types'

const MEMBER_QUERY_KEYS = [
  ['point-ledgers'],
  ['lottery-status'],
  ['h5-checkin-status'],
  ['h5-card-plans'],
  ['referral-overview']
] as const

const ORDER_QUERY_KEYS = [
  ['orders'],
  ['recharge-order'],
  ['h5-card-order']
] as const

export function useH5Realtime() {
  const { member, setMember } = useApp()
  const client = useQueryClient()

  useEffect(() => {
    if (!member?.id) return

    let stopped = false
    let needsVisibleSync = false
    let lastVisibleSyncAt = 0
    let memberTimer = 0
    let orderTimer = 0
    let leaderboardTimer = 0
    let authTimer = 0
    let memberRefreshRunning = false
    let memberRefreshPending = false

    const visible = () => document.visibilityState === 'visible'
    const deferWhenHidden = () => {
      if (visible()) return false
      needsVisibleSync = true
      return true
    }
    const invalidate = (keys: readonly (readonly string[])[]) => {
      for (const queryKey of keys) {
        void client.invalidateQueries({ queryKey: [...queryKey], refetchType: 'active' })
      }
    }
    const refreshMember = async () => {
      if (stopped || deferWhenHidden()) return
      if (memberRefreshRunning) {
        memberRefreshPending = true
        return
      }
      memberRefreshRunning = true
      try {
        const updated = await api<Member>('/api/h5/me')
        if (!stopped) setMember(updated)
      } catch {
        // Reconnect, visibility sync, and the manual refresh remain retry paths.
      } finally {
        memberRefreshRunning = false
        if (memberRefreshPending && !stopped) {
          memberRefreshPending = false
          memberTimer = window.setTimeout(() => void refreshMember(), 100)
        }
      }
    }
    const scheduleMemberRefresh = () => {
      if (deferWhenHidden()) return
      window.clearTimeout(memberTimer)
      memberTimer = window.setTimeout(() => {
        void refreshMember()
        if (!deferWhenHidden()) invalidate(MEMBER_QUERY_KEYS)
      }, 100)
    }
    const scheduleOrderRefresh = () => {
      if (deferWhenHidden()) return
      window.clearTimeout(orderTimer)
      orderTimer = window.setTimeout(() => invalidate(ORDER_QUERY_KEYS), 150)
    }
    const scheduleLeaderboardRefresh = () => {
      if (deferWhenHidden()) return
      window.clearTimeout(leaderboardTimer)
      leaderboardTimer = window.setTimeout(() => {
        void client.invalidateQueries({ queryKey: ['leaderboard'], refetchType: 'active' })
      }, 250 + Math.floor(Math.random() * 500))
    }
    const syncVisibleState = () => {
      if (!visible()) {
        needsVisibleSync = true
        return
      }
      const now = Date.now()
      if (!needsVisibleSync && now - lastVisibleSyncAt < 1000) return
      needsVisibleSync = false
      lastVisibleSyncAt = now
      scheduleMemberRefresh()
      invalidate(ORDER_QUERY_KEYS)
      void client.invalidateQueries({ queryKey: ['leaderboard'], refetchType: 'active' })
    }
    const verifySessionAfterError = () => {
      if (stopped || !visible()) return
      window.clearTimeout(authTimer)
      authTimer = window.setTimeout(async () => {
        try {
          const current = await api<Member>('/api/h5/auth/session')
          if (!stopped) setMember(current)
        } catch (error) {
          if (!stopped && error instanceof ApiError && error.status === 401) setMember(null)
        }
      }, 5000)
    }

    const source = new EventSource(apiUrl('/api/h5/events'), { withCredentials: true })
    const handleReady = () => {
      window.clearTimeout(authTimer)
      syncVisibleState()
    }
    const handleMemberChanged = () => {
      scheduleMemberRefresh()
    }
    source.addEventListener('ready', handleReady)
    source.addEventListener('member-snapshot-changed', handleMemberChanged)
    source.addEventListener('order-changed', scheduleOrderRefresh)
    source.addEventListener('leaderboard-changed', scheduleLeaderboardRefresh)
    source.addEventListener('error', verifySessionAfterError)
    document.addEventListener('visibilitychange', syncVisibleState)
    window.addEventListener('pageshow', syncVisibleState)
    window.addEventListener('focus', syncVisibleState)

    return () => {
      stopped = true
      source.close()
      window.clearTimeout(memberTimer)
      window.clearTimeout(orderTimer)
      window.clearTimeout(leaderboardTimer)
      window.clearTimeout(authTimer)
      document.removeEventListener('visibilitychange', syncVisibleState)
      window.removeEventListener('pageshow', syncVisibleState)
      window.removeEventListener('focus', syncVisibleState)
    }
  }, [client, member?.id, setMember])
}
